import { hasConsent } from "@/lib/cookieConsent";
import { MARKETING_SITE_ANALYTICS_ID } from "./constants";

export type WebsiteAnalyticsPayload = {
  rental_website_id: string;
  visitor_id: string;
  session_id: string;
  is_new_session?: boolean;
  event_type?: "pageview" | "duration" | "click";
  duration_seconds?: number;
  /** pageview only */
  path?: string;
  viewport_width?: number;
  referrer?: string | null;
  utm_source?: string | null;
  utm_medium?: string | null;
  utm_campaign?: string | null;
  /** click only */
  click_label?: string;
};

function analyticsHeaders(): Record<string, string> | null {
  const key = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string | undefined;
  if (!key) return null;
  return {
    "Content-Type": "application/json",
    apikey: key,
    Authorization: `Bearer ${key}`,
  };
}

/**
 * Verejné meranie — vyžaduje anon key (raw fetch bez neho vracia 401).
 *
 * Single consent gate for pageview/duration/click, mirroring how trackLead/trackEvent in
 * fbpixel.ts effectively no-op before marketing consent (there window.fbq simply doesn't
 * exist yet; here there's no equivalent SDK-presence signal, so the check lives here
 * directly). Nothing is sent to record-website-analytics without analytics consent.
 */
export function recordWebsiteAnalytics(
  payload: WebsiteAnalyticsPayload,
  options?: { keepalive?: boolean },
): void {
  if (!hasConsent("analytics")) return;

  const url = import.meta.env.VITE_SUPABASE_URL as string | undefined;
  const headers = analyticsHeaders();
  if (!url || !headers) return;

  void fetch(`${url}/functions/v1/record-website-analytics`, {
    method: "POST",
    headers,
    body: JSON.stringify(payload),
    keepalive: options?.keepalive ?? false,
  }).catch(() => {});
}

/**
 * Fire-and-forget CTA/form-button click tracking for webnaprenajom.sk itself. No-ops the same
 * way recordWebsiteAnalytics does pre-consent. Reuses the visitor/session ids already tracked
 * for pageviews so a click can be correlated to the same session in future analysis.
 */
export function trackCtaClick(label: string): void {
  if (typeof window === "undefined" || !label) return;
  const siteId =
    (import.meta.env.VITE_MARKETING_ANALYTICS_SITE_ID as string | undefined)?.trim() ||
    MARKETING_SITE_ANALYTICS_ID;
  const visitorKey = `wnp_v_${siteId}`;
  const sessionKey = `wnp_s_${siteId}`;
  let visitorId: string;
  let sessionId: string;
  try {
    visitorId = localStorage.getItem(visitorKey) ?? "anon";
    sessionId = sessionStorage.getItem(sessionKey) ?? "anon";
  } catch {
    visitorId = "anon";
    sessionId = "anon";
  }

  recordWebsiteAnalytics({
    rental_website_id: siteId,
    visitor_id: visitorId,
    session_id: sessionId,
    event_type: "click",
    click_label: label,
  });
}
